import { useState } from "react";
import { X, ShoppingBag, ArrowUpRight } from "lucide-react";
import { useCart } from "@/state/CartContext";
import { api } from "@/lib/api";
import { redirectToStripe } from "@/lib/redirect";
import { toast } from "sonner";

const CITY_LABEL = {
  sydney: "Sydney",
  melbourne: "Melbourne",
  adelaide: "Adelaide",
  london: "London",
  paris: "Paris",
};
const CITY_DATE = {
  sydney: "1 July 2026",
  melbourne: "Tue 11 Aug 2026",
  adelaide: "Sun 9 Aug 2026",
  london: "15 July 2026",
  paris: "17 July 2026",
};

function fmt(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

// Shipping zones mirror the backend (server.py VINYL_SHIPPING_OPTIONS):
// ANZ/UK = $15, EU/NA/RoW = $20.
const VINYL_SHIPPING_ZONES = {
  "15": new Set(["AU", "NZ", "GB"]),
  "20": new Set([
    // EU
    "IE", "FR", "DE", "IT", "ES", "NL", "BE", "AT", "PT", "SE", "NO", "DK",
    "FI", "CH", "PL", "CZ", "GR", "HU", "RO",
    // NA
    "US", "CA",
    // RoW (we treat anything else as $20 too — same as backend ROW bucket)
  ]),
};
function vinylShippingFor(country) {
  const cc = (country || "").toUpperCase();
  if (VINYL_SHIPPING_ZONES["15"].has(cc)) return { cost: 15, label: cc === "GB" ? "United Kingdom · Tracked" : "Australia & New Zealand · Tracked" };
  return { cost: 20, label: "International · Tracked" };
}

export default function CartDrawer() {
  const { items, open, setOpen, subtotal, hasVinyl, hasWav, setQuantity, updateWavPrice, remove, keyOf, clear } = useCart();
  const [stage, setStage] = useState("cart"); // 'cart' | 'address'
  const [addr, setAddr] = useState({ name: "", email: "", line1: "", line2: "", city: "", state: "", postcode: "", country: "AU" });
  const [checkingOut, setCheckingOut] = useState(false);

  // Mirror the backend's 20% bulk discount when vinyl qty ≥ 2.
  const vinylLine = items.find((i) => i.type === "vinyl");
  const vinylQty = vinylLine?.quantity || 0;
  const vinylDiscount = vinylQty >= 2 ? Math.round(60 * vinylQty * 0.20 * 100) / 100 : 0;

  const shippingCost = hasVinyl ? vinylShippingFor(addr.country).cost : 0;
  const total = subtotal - vinylDiscount + shippingCost;

  const startCheckout = async () => {
    if (items.length === 0) return;
    if (hasVinyl && stage !== "address") {
      setStage("address");
      return;
    }
    if (hasVinyl) {
      if (!addr.name || !addr.line1 || !addr.city || !addr.postcode || !addr.country) {
        toast.error("Please complete the shipping address.");
        return;
      }
    }
    setCheckingOut(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        email: addr.email || undefined,
        items: items.map((i) => {
          if (i.type === "ticket") return { type: "ticket", city: i.city, quantity: i.quantity || 1 };
          if (i.type === "vinyl") return { type: "vinyl", quantity: i.quantity || 1 };
          if (i.type === "wav") return { type: "wav", custom_price: Number(i.custom_price || 0) };
          if (i.type === "myth_film") return { type: "myth_film", custom_price: Number(i.custom_price || 0) };
          return i;
        }),
      };
      if (hasVinyl) {
        payload.shipping_address = {
          name: addr.name,
          line1: addr.line1,
          line2: addr.line2 || undefined,
          city: addr.city,
          state: addr.state || undefined,
          postcode: addr.postcode,
          country: addr.country.toUpperCase(),
        };
      }
      const { data } = await api.post("/checkout/cart", payload);
      redirectToStripe(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setCheckingOut(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/75 backdrop-blur-sm z-40 animate-in fade-in duration-300"
        onClick={() => setOpen(false)}
        data-testid="cart-backdrop"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:max-w-md bg-black border-l border-white/15 flex flex-col"
        data-testid="cart-drawer"
      >
        <header className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <h2 className="vintage-text text-2xl flex items-center gap-3">
            <ShoppingBag className="w-4 h-4" strokeWidth={1.2} />
            <span>Your Cart</span>
          </h2>
          <button onClick={() => { setOpen(false); setStage("cart"); }} aria-label="Close cart" data-testid="cart-close">
            <X className="w-5 h-5 text-white/70 hover:text-white" strokeWidth={1.2} />
          </button>
        </header>

        {stage === "cart" ? (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-testid="cart-items">
              {items.length === 0 ? (
                <p className="text-center text-white/40 font-serif italic mt-20" data-testid="cart-empty">
                  Your cart is empty.
                </p>
              ) : (
                items.map((it) => {
                  const k = keyOf(it);
                  if (it.type === "wav") {
                    return (
                      <div key={k} className="border-b border-white/10 pb-5" data-testid={`cart-item-wav`}>
                        <div className="flex items-baseline justify-between gap-3 mb-3">
                          <div>
                            <p className="vintage-text text-xl">Pre-order Metamorph Package</p>
                            <p className="font-serif italic text-xs text-white/45">Pay what you can — emailed on release</p>
                          </div>
                          <button onClick={() => remove(k)} className="text-white/40 hover:text-white text-xs uppercase tracking-[0.16em]" data-testid="cart-remove-wav">
                            Remove
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="vintage-text text-2xl opacity-60">$</span>
                          <input
                            type="number"
                            min="1"
                            step="0.5"
                            value={it.custom_price ?? ""}
                            onChange={(e) => updateWavPrice(e.target.value)}
                            className="bg-transparent border-b border-white/20 focus:border-white outline-none vintage-text text-3xl w-28 tabular-nums"
                            data-testid="cart-wav-price"
                          />
                          <span className="font-mono text-[10px] text-white/45 tracking-[0.3em] ml-1 pb-1">AUD</span>
                        </div>
                      </div>
                    );
                  }
                  if (it.type === "myth_film") {
                    return (
                      <div key={k} className="border-b border-white/10 pb-5" data-testid={`cart-item-myth-film`}>
                        <div className="flex items-baseline justify-between gap-3 mb-3">
                          <div>
                            <p className="vintage-text text-xl">Myth · Live Performance Film</p>
                            <p className="font-serif italic text-xs text-white/45">Limited download — pay what you can</p>
                          </div>
                          <button onClick={() => remove(k)} className="text-white/40 hover:text-white text-xs uppercase tracking-[0.16em]" data-testid="cart-remove-myth-film">
                            Remove
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="vintage-text text-2xl opacity-60">$</span>
                          <input
                            type="number"
                            min="1"
                            step="0.5"
                            value={it.custom_price ?? ""}
                            onChange={(e) =>
                              // re-use add to overwrite custom_price for the singleton
                              undefined
                            }
                            readOnly
                            className="bg-transparent border-b border-white/20 outline-none vintage-text text-3xl w-28 tabular-nums opacity-90"
                            data-testid="cart-myth-film-price"
                          />
                          <span className="font-mono text-[10px] text-white/45 tracking-[0.3em] ml-1 pb-1">AUD</span>
                        </div>
                      </div>
                    );
                  }
                  const isTicket = it.type === "ticket";
                  const label = isTicket ? `Metamorph · ${CITY_LABEL[it.city]}` : "Metamorph · Vinyl LP";
                  const sub = isTicket ? CITY_DATE[it.city] : "Limited edition · 12-inch LP";
                  const unit = isTicket ? ({ sydney: 100, melbourne: 100, adelaide: 100, london: 85, paris: 115 })[it.city] : 60;
                  return (
                    <div key={k} className="border-b border-white/10 pb-5" data-testid={`cart-item-${k.replace(":", "-")}`}>
                      <div className="flex items-baseline justify-between gap-3 mb-2">
                        <div>
                          <p className="vintage-text text-xl leading-tight">{label}</p>
                          <p className="font-serif italic text-xs text-white/45">{sub}</p>
                        </div>
                        <button onClick={() => remove(k)} className="text-white/40 hover:text-white text-xs uppercase tracking-[0.16em]" data-testid={`cart-remove-${k.replace(":", "-")}`}>
                          Remove
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-white/15">
                          <button
                            onClick={() => setQuantity(k, Math.max(1, (it.quantity || 1) - 1))}
                            disabled={(it.quantity || 1) <= 1}
                            className="px-3 py-1.5 hover:bg-white/5 disabled:opacity-30"
                            aria-label="Decrease"
                            data-testid={`cart-qty-minus-${k.replace(":", "-")}`}
                          >−</button>
                          <span className="px-4 vintage-text tabular-nums" data-testid={`cart-qty-${k.replace(":", "-")}`}>{it.quantity || 1}</span>
                          <button
                            onClick={() => setQuantity(k, (it.quantity || 1) + 1)}
                            className="px-3 py-1.5 hover:bg-white/5"
                            aria-label="Increase"
                            data-testid={`cart-qty-plus-${k.replace(":", "-")}`}
                          >+</button>
                        </div>
                        <span className="font-mono tabular-nums text-sm">
                          {fmt(unit * (it.quantity || 1))}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {items.length > 0 && (
              <footer className="border-t border-white/10 px-6 py-5 space-y-4" data-testid="cart-footer">
                {(() => {
                  return null;
                })()}
                <div className="flex justify-between text-sm">
                  <span className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-70">Subtotal</span>
                  <span className="font-mono tabular-nums" data-testid="cart-subtotal">{fmt(subtotal)} AUD</span>
                </div>
                {vinylDiscount > 0 && (
                  <div className="flex justify-between text-sm" data-testid="cart-vinyl-discount">
                    <span className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-70 text-[var(--mm-accent)]">
                      Vinyl bulk · 20% off
                    </span>
                    <span className="font-mono tabular-nums text-[var(--mm-accent)]">
                      −{fmt(vinylDiscount)}
                    </span>
                  </div>
                )}
                {hasVinyl && (
                  <p className="text-xs text-white/40 italic">
                    Shipping calculated on the next step.
                  </p>
                )}
                <button
                  onClick={startCheckout}
                  disabled={items.length === 0 || checkingOut}
                  className="btn-line btn-filled w-full justify-center"
                  data-testid="cart-checkout"
                >
                  {checkingOut ? "Opening…" : hasVinyl ? "Continue · Address" : "Checkout"}
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1} />
                </button>
                <p className="text-[9px] text-center text-white/45 uppercase tracking-[0.24em]">
                  Apple Pay · Google Pay · Card
                </p>
              </footer>
            )}
          </>
        ) : (
          /* ─── Address stage (only when vinyl present) ─── */
          <>
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4" data-testid="cart-address-form">
              <p className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-70">Where shall we send the vinyl?</p>
              <input className="line-input" placeholder="Full name" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="addr-name" />
              <input className="line-input" placeholder="Email (for shipping updates)" type="email" value={addr.email} onChange={(e) => setAddr({ ...addr, email: e.target.value })} data-testid="addr-email" />
              <input className="line-input" placeholder="Address line 1" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="addr-line1" />
              <input className="line-input" placeholder="Address line 2 (optional)" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} data-testid="addr-line2" />
              <div className="grid grid-cols-2 gap-4">
                <input className="line-input" placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="addr-city" />
                <input className="line-input" placeholder="State / Region" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} data-testid="addr-state" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input className="line-input" placeholder="Postcode" value={addr.postcode} onChange={(e) => setAddr({ ...addr, postcode: e.target.value })} data-testid="addr-postcode" />
                <select
                  className="line-input bg-black"
                  value={addr.country}
                  onChange={(e) => setAddr({ ...addr, country: e.target.value })}
                  data-testid="addr-country"
                >
                  <optgroup label="$15 shipping">
                    <option value="AU">Australia</option>
                    <option value="NZ">New Zealand</option>
                    <option value="GB">United Kingdom</option>
                  </optgroup>
                  <optgroup label="$20 shipping · Europe">
                    <option value="IE">Ireland</option>
                    <option value="FR">France</option>
                    <option value="DE">Germany</option>
                    <option value="IT">Italy</option>
                    <option value="ES">Spain</option>
                    <option value="NL">Netherlands</option>
                    <option value="BE">Belgium</option>
                    <option value="AT">Austria</option>
                    <option value="PT">Portugal</option>
                    <option value="SE">Sweden</option>
                    <option value="NO">Norway</option>
                    <option value="DK">Denmark</option>
                    <option value="FI">Finland</option>
                    <option value="CH">Switzerland</option>
                    <option value="PL">Poland</option>
                    <option value="CZ">Czechia</option>
                    <option value="GR">Greece</option>
                    <option value="HU">Hungary</option>
                    <option value="RO">Romania</option>
                    <option value="BG">Bulgaria</option>
                  </optgroup>
                  <optgroup label="$20 shipping · North America">
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                  </optgroup>
                  <optgroup label="$20 shipping · Rest of world">
                    <option value="JP">Japan</option>
                    <option value="SG">Singapore</option>
                    <option value="HK">Hong Kong</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="MX">Mexico</option>
                    <option value="IL">Israel</option>
                    <option value="ZA">South Africa</option>
                    <option value="TH">Thailand</option>
                    <option value="KR">South Korea</option>
                    <option value="TW">Taiwan</option>
                    <option value="MY">Malaysia</option>
                    <option value="ID">Indonesia</option>
                    <option value="PH">Philippines</option>
                    <option value="IN">India</option>
                    <option value="VN">Vietnam</option>
                    <option value="BR">Brazil</option>
                    <option value="AR">Argentina</option>
                    <option value="CL">Chile</option>
                    <option value="CO">Colombia</option>
                    <option value="PE">Peru</option>
                    <option value="UY">Uruguay</option>
                    <option value="EC">Ecuador</option>
                    <option value="BO">Bolivia</option>
                    <option value="PY">Paraguay</option>
                    <option value="VE">Venezuela</option>
                    <option value="GY">Guyana</option>
                    <option value="SR">Suriname</option>
                  </optgroup>
                </select>
              </div>

              <div className="border border-white/10 p-4 mt-4 text-sm space-y-2" data-testid="cart-totals">
                <div className="flex justify-between text-white/65">
                  <span>Subtotal</span>
                  <span className="font-mono tabular-nums">{fmt(subtotal)}</span>
                </div>
                {vinylDiscount > 0 && (
                  <div className="flex justify-between text-[var(--mm-accent)]" data-testid="cart-totals-discount">
                    <span>Vinyl bulk · 20% off</span>
                    <span className="font-mono tabular-nums">−{fmt(vinylDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-white/65">
                  <span>{vinylShippingFor(addr.country).label}</span>
                  <span className="font-mono tabular-nums">{fmt(shippingCost)}</span>
                </div>
                <div className="flex justify-between vintage-text text-xl pt-2 border-t border-white/10">
                  <span>Total</span>
                  <span className="font-mono tabular-nums" data-testid="cart-total">{fmt(total)} AUD</span>
                </div>
              </div>
            </div>
            <footer className="border-t border-white/10 px-6 py-5 space-y-3">
              <button onClick={startCheckout} disabled={checkingOut} className="btn-line btn-filled w-full justify-center" data-testid="cart-checkout-final">
                {checkingOut ? "Opening…" : `Pay ${fmt(total)} AUD`}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1} />
              </button>
              <button onClick={() => setStage("cart")} className="vintage-link text-xs uppercase block w-full text-center opacity-70 hover:opacity-100" data-testid="cart-back">
                ← Back to cart
              </button>
            </footer>
          </>
        )}
      </aside>
    </>
  );
}
